import React, { useContext } from "react";
import { Box } from "@mui/material";
import { colors } from "../../../styles/colors";
import { borderRadius, formField } from "../../../styles/theme";
import { ButtonDensityContext } from "./Button";

/**
 * Im Section-Kopf dieselben Maße wie ein kompakter Knopf (`Button`): Innenabstand, Schrift und
 * Zeilenhöhe. Die Höhe ergibt sich daraus, statt als Zahl danebenzustehen, die beim nächsten
 * Umbau des Knopfs nicht mitwandert.
 */
const COMPACT_SEGMENT = {
  padding: "3px 12px",
  fontSize: "0.8125rem",
  lineHeight: 1.75,
} as const;

interface ToggleOption {
  value: string;
  label: string;
}

interface ToggleSwitchProps {
  value: string;
  options: [ToggleOption, ToggleOption];
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Binärer Toggle im gleichen visuellen Stil wie InputField / InputFieldNumber: borderRadius.field,
 * identischer Schatten und Border. Die gewählte Seite sieht aus wie ein sekundärer Knopf –
 * umrandet, ohne Füllung –, die andere bleibt schlicht.
 */
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  value,
  options,
  onChange,
  disabled = false,
}) => {
  // Neben Feldern so hoch wie ein Feld, im Section-Kopf so hoch wie die Knöpfe dort.
  const compact = useContext(ButtonDensityContext) === "compact";
  return (
    <Box
      role="radiogroup"
      sx={{
        display: "inline-flex",
        // Kein MUI-Feld, soll aber in einer Zeile mit Feldern gleich hoch sein. Im Section-Kopf
        // bestimmen die Seiten die Höhe – genau wie beim Knopf daneben.
        height: compact ? undefined : formField.height,
        borderRadius: `${borderRadius.field}px`,
        border: `1px solid ${colors.border.default}`,
        boxShadow: disabled ? "none" : `0 2px 4px ${colors.shadow.light}`,
        transition: "box-shadow 0.2s ease-in-out",
        opacity: disabled ? 0.6 : 1,
        "&:hover": disabled
          ? {}
          : {
              boxShadow: `0 4px 8px ${colors.shadow.medium}`,
            },
      }}
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        const select = () => {
          if (!disabled && !isSelected) onChange(option.value);
        };
        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={isSelected}
            tabIndex={disabled ? -1 : isSelected ? 0 : -1}
            onClick={select}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                select();
              } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                e.preventDefault();
                const other = options.find((o) => o.value !== option.value);
                if (other && !disabled) onChange(other.value);
              }
            }}
            sx={{
              // Beide Beschriftungen liegen in derselben Zelle: Die unsichtbare fette reserviert
              // die Breite, damit die Seite beim Wählen nicht springt.
              display: "grid",
              alignItems: "center",
              justifyItems: "center",
              ...(compact ? COMPACT_SEGMENT : { px: 2, fontSize: "0.875rem" }),
              // Der Rahmen der gewählten Seite liegt auf dem des Umschalters, nicht daneben.
              m: "-1px",
              border: `1px solid ${isSelected ? colors.button.secondary.border : "transparent"}`,
              borderRadius: `${borderRadius.field}px`,
              fontWeight: isSelected ? 500 : 400,
              whiteSpace: "nowrap",
              cursor: disabled ? "default" : "pointer",
              userSelect: "none",
              transition: "all 0.15s ease-in-out",
              backgroundColor: "transparent",
              color: isSelected
                ? colors.button.secondary.text
                : colors.text.secondary,
              "&:hover":
                disabled || isSelected
                  ? {}
                  : { backgroundColor: colors.state.hover },
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{ gridArea: "1 / 1", fontWeight: 500, visibility: "hidden" }}
            >
              {option.label}
            </Box>
            <Box component="span" sx={{ gridArea: "1 / 1" }}>
              {option.label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ToggleSwitch;
